import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Check, ListTodo, Plus, Target, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  MONTHS_DE, DAYS_SHORT, HOURS, HOUR_HEIGHT,
  APPOINTMENT_COLORS, STORAGE_KEYS, getAppointmentColor
} from "@/utils/calendarConstants";
import {
  getLocalDateStr, getWeekNumber, getWeekDates, loadData
} from "@/utils/calendarUtils";
import AppointmentModal from "@/components/calendar/AppointmentModal";
import { userStorage } from '../../utils/userStorage';
export default function WeekView({ currentDate, weekGoals, onWeekGoalChange, dayFocus, onDayFocusChange, appointments, onAppointmentChange, onDeleteFromAll, googleCalendars, primaryCalendar, viewMode = 'calendar', todoCalendarData, onTodoCalendarHandled, onEditTodo, todoEditRefreshKey = 0, onToggleAppointmentDone }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalText, setGoalText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editingFocus, setEditingFocus] = useState(null);
  const [showCalendarTip, setShowCalendarTip] = useState(() => userStorage.getItem('burningteam_hide_calendar_tip') !== 'true');
  const [focusText, setFocusText] = useState('');
  const [showDayTodosModal, setShowDayTodosModal] = useState(false);
  const [dayTodosDate, setDayTodosDate] = useState(null);
  const [newDayTodoText, setNewDayTodoText] = useState('');
  const [dayTodosRefresh, setDayTodosRefresh] = useState(0); // Force re-render
  const [showWeekTodosOverview, setShowWeekTodosOverview] = useState(false); // Week overview modal
  const [newWeekTodoText, setNewWeekTodoText] = useState('');
  const [selectedWeekday, setSelectedWeekday] = useState(0); // For adding new todo to specific day
  const [showMonthTodosOverview, setShowMonthTodosOverview] = useState(false); // Month overview modal
  const [newMonthTodoText, setNewMonthTodoText] = useState('');
  const [selectedMonthWeek, setSelectedMonthWeek] = useState(null); // For adding new todo to specific KW
  const [pendingTransferTodo, setPendingTransferTodo] = useState(null); // For transfer-complete event
  const scrollContainerRef = useRef(null); // Header horizontal scroll
  const timeGridRef = useRef(null); // Body vertical scroll
  const bodyScrollRef = useRef(null); // Body horizontal scroll
  
  const weekDates = getWeekDates(currentDate);
  const weekNumber = getWeekNumber(currentDate);
  const weekKey = `${currentDate.getFullYear()}-W${weekNumber}`;
  const today = new Date();
  const todayStr = getLocalDateStr(today);
  const lastDayMonth = weekDates[6].getMonth();
  
  const firstDayMonth = weekDates[0].getMonth();
  const monthDisplay = firstDayMonth === lastDayMonth 
    ? MONTHS_DE[firstDayMonth]
    : `${MONTHS_DE[firstDayMonth]} / ${MONTHS_DE[lastDayMonth]}`;
  
  useEffect(() => {
    setGoalText(weekGoals[weekKey] || '');
  }, [weekKey, weekGoals]);

  // Clear pending transfer when modal closes (cancel case - todo stays in list)
  useEffect(() => {
    if (!showModal) setPendingTransferTodo(null);
  }, [showModal]);

  // Handle todo-to-calendar transfer
  useEffect(() => {
    if (todoCalendarData && todoCalendarData.type === 'week') {
      // Set selected date from todo dueDate or today
      const dateStr = todoCalendarData.dueDate || todayStr;
      setSelectedDate(dateStr);
      setSelectedTime('09:00');
      setEditingAppointment({
        id: null,
        title: todoCalendarData.title || '',
        date: dateStr,
        startTime: '09:00',
        endTime: '10:00',
        priority: todoCalendarData.priority || 5,
        note: todoCalendarData.note || '',
        color: null,
        contactRef: todoCalendarData.contactRef || null,
      });
      // Store pending transfer info so we can delete todo AFTER successful save
      if (todoCalendarData.todoId) {
        setPendingTransferTodo({ id: todoCalendarData.todoId, fromTab: todoCalendarData.fromTab });
      }
      setShowModal(true);
      // Clear the data
      if (onTodoCalendarHandled) {
        onTodoCalendarHandled();
      }
    }
  }, [todoCalendarData, todayStr, onTodoCalendarHandled]);

  // Day Todos helpers - direct localStorage access

  const openDayTodosModal = (dateKey) => {
    setDayTodosDate(dateKey);
    setNewDayTodoText('');
    setDayTodosRefresh(r => r + 1); // Force re-render to get fresh data
    setShowDayTodosModal(true);
  };

  const getDayTodos = (dateKey) => {
    if (!dateKey) return [];
    const isToday = dateKey === todayStr;
    
    if (isToday) {
      // For today, show dailyTodos from localStorage
      const today = getLocalDateStr();
      const stored = loadData(STORAGE_KEYS.dailyTodos, {});
      const dailyTodos = stored[today] || [];
      return dailyTodos.map(t => ({ ...t, source: 'daily' }));
    } else {
      // For other days, show weeklyTodos with matching weekday from localStorage
      const now = new Date();
      const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
      const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
      const weeklyTodos = stored[weekKey] || [];
      
      const date = new Date(dateKey);
      const weekdayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1; // Mo=0, Di=1, etc.
      return weeklyTodos
        .filter(t => t.weekday === weekdayIndex)
        .map(t => ({ ...t, source: 'weekly' }));
    }
  };

  const addDayTodo = () => {
    if (!newDayTodoText.trim() || !dayTodosDate) return;
    // Block adding todos for past days
    const todoDate = new Date(dayTodosDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    todoDate.setHours(0, 0, 0, 0);
    if (todoDate < today) return;
    
    const isToday = dayTodosDate === todayStr;
    const newTodo = {
      id: Date.now(),
      text: newDayTodoText.trim(),
      done: false,
      priority: 5,
      createdAt: new Date().toISOString()
    };
    
    if (isToday) {
      // Add to dailyTodos in localStorage
      const today = getLocalDateStr();
      const stored = loadData(STORAGE_KEYS.dailyTodos, {});
      const dailyTodos = stored[today] || [];
      stored[today] = [...dailyTodos, newTodo];
      userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
    } else {
      // Add to weeklyTodos with weekday in localStorage
      const now = new Date();
      const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
      const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
      const weeklyTodos = stored[weekKey] || [];
      
      const date = new Date(dayTodosDate);
      const weekdayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;
      const todoWithWeekday = { ...newTodo, weekday: weekdayIndex };
      stored[weekKey] = [...weeklyTodos, todoWithWeekday];
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    }
    
    setNewDayTodoText('');
    setDayTodosRefresh(r => r + 1); // Force re-render
  };

  const toggleDayTodo = (todoId, source) => {
    if (source === 'daily') {
      const today = getLocalDateStr();
      const stored = loadData(STORAGE_KEYS.dailyTodos, {});
      const dailyTodos = stored[today] || [];
      stored[today] = dailyTodos.map(t => 
        t.id === todoId ? { ...t, done: !t.done } : t
      );
      userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
    } else {
      const now = new Date();
      const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
      const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
      const weeklyTodos = stored[weekKey] || [];
      stored[weekKey] = weeklyTodos.map(t => 
        t.id === todoId ? { ...t, done: !t.done } : t
      );
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    }
    setDayTodosRefresh(r => r + 1); // Force re-render
  };

  // Get all todos for the entire week, grouped by weekday
  const getWeekTodosGrouped = () => {
    const now = new Date();
    const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
    const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
    const weeklyTodos = stored[weekKey] || [];
    
    // Also get today's daily todos
    const today = getLocalDateStr(now);
    const dailyStored = loadData(STORAGE_KEYS.dailyTodos, {});
    const dailyTodos = (dailyStored[today] || []).map(t => ({ ...t, source: 'daily' }));
    
    // Group by weekday (0=Mo, 1=Di, etc.) + unassigned (-1)
    const grouped = { unassigned: [] };
    for (let i = 0; i < 7; i++) {
      grouped[i] = [];
    }
    
    // Add weekly todos - check if weekday is assigned
    weeklyTodos.forEach(t => {
      if (t.weekday === undefined || t.weekday === null || t.weekday === -1) {
        grouped.unassigned.push({ ...t, source: 'weekly' });
      } else {
        const day = t.weekday;
        if (!grouped[day]) grouped[day] = [];
        grouped[day].push({ ...t, source: 'weekly' });
      }
    });
    
    // Add daily todos to today's weekday
    const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1;
    dailyTodos.forEach(t => {
      grouped[todayWeekday].push(t);
    });
    
    return grouped;
  };

  // Add todo to specific weekday (or unassigned with 'unassigned')
  const addWeekTodo = (weekdayIndex) => {
    if (!newWeekTodoText.trim()) return;
    
    const now = new Date();
    const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1;
    
    const newTodo = {
      id: Date.now(),
      text: newWeekTodoText.trim(),
      done: false,
      priority: 5,
      createdAt: new Date().toISOString()
    };
    
    if (weekdayIndex === todayWeekday) {
      // Add to dailyTodos
      const today = getLocalDateStr(now);
      const stored = loadData(STORAGE_KEYS.dailyTodos, {});
      const dailyTodos = stored[today] || [];
      stored[today] = [...dailyTodos, newTodo];
      userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
    } else {
      // Add to weeklyTodos with weekday (or undefined for unassigned)
      const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
      const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
      const weeklyTodos = stored[weekKey] || [];
      const todoWithWeekday = weekdayIndex === 'unassigned' 
        ? { ...newTodo } // No weekday = unassigned
        : { ...newTodo, weekday: weekdayIndex };
      stored[weekKey] = [...weeklyTodos, todoWithWeekday];
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    }
    
    setNewWeekTodoText('');
    setDayTodosRefresh(r => r + 1);
  };

  // Toggle todo in week overview
  const toggleWeekTodo = (todoId, source, weekdayIndex) => {
    const now = new Date();
    
    if (source === 'daily') {
      const today = getLocalDateStr(now);
      const stored = loadData(STORAGE_KEYS.dailyTodos, {});
      const dailyTodos = stored[today] || [];
      stored[today] = dailyTodos.map(t => 
        t.id === todoId ? { ...t, done: !t.done } : t
      );
      userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
    } else {
      const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
      const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
      const weeklyTodos = stored[weekKey] || [];
      stored[weekKey] = weeklyTodos.map(t => 
        t.id === todoId ? { ...t, done: !t.done } : t
      );
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    }
    setDayTodosRefresh(r => r + 1);
  };

  // Delete todo from week overview
  const deleteWeekTodo = (todoId, source) => {
    const now = new Date();
    
    if (source === 'daily') {
      const today = getLocalDateStr(now);
      const stored = loadData(STORAGE_KEYS.dailyTodos, {});
      const dailyTodos = stored[today] || [];
      stored[today] = dailyTodos.filter(t => t.id !== todoId);
      userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
    } else {
      const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
      const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
      const weeklyTodos = stored[weekKey] || [];
      stored[weekKey] = weeklyTodos.filter(t => t.id !== todoId);
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    }
    setDayTodosRefresh(r => r + 1);
  };

  // Get all todos for the current month, grouped by KW
  const getMonthTodosGrouped = () => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const stored = loadData(STORAGE_KEYS.monthlyTodos, {});
    const monthTodos = Array.isArray(stored) ? stored : (stored[monthKey] || []);
    
    const weeksInMonth = getWeeksInCurrentMonth();
    const grouped = { unassigned: [] };
    
    weeksInMonth.forEach(week => {
      grouped[week.weekNum] = [];
    });
    
    monthTodos.forEach(t => {
      if (t.weekNum === undefined || t.weekNum === null) {
        grouped.unassigned.push(t);
      } else if (grouped[t.weekNum]) {
        grouped[t.weekNum].push(t);
      } else {
        // If weekNum doesn't match any week in current month, add to unassigned
        grouped.unassigned.push(t);
      }
    });
    
    return grouped;
  };

  // Add todo to specific KW in month
  const addMonthTodo = (weekNumOrUnassigned) => {
    if (!newMonthTodoText.trim()) return;
    
    const newTodo = {
      id: Date.now(),
      text: newMonthTodoText.trim(),
      done: false,
      priority: 5,
      createdAt: new Date().toISOString()
    };
    
    if (weekNumOrUnassigned === 'unassigned') {
      // No weekNum = "Diesen Monat"
      setMonthlyTodos(prev => [...prev, newTodo]);
    } else {
      // With weekNum
      const todoWithWeek = { ...newTodo, weekNum: weekNumOrUnassigned };
      setMonthlyTodos(prev => [...prev, todoWithWeek]);
    }
    
    setNewMonthTodoText('');
    setDayTodosRefresh(r => r + 1);
  };

  // Toggle todo in month overview
  const toggleMonthTodo = (todoId) => {
    setMonthlyTodos(prev => prev.map(t => 
      t.id === todoId ? { ...t, done: !t.done } : t
    ));
    setDayTodosRefresh(r => r + 1);
  };

  // Delete todo from month overview
  const deleteMonthTodo = (todoId) => {
    setMonthlyTodos(prev => prev.filter(t => t.id !== todoId));
    setDayTodosRefresh(r => r + 1);
  };

  // Priority helper functions
  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'bg-red-500 hover:bg-red-600';
    if (priority >= 5) return 'bg-amber-500 hover:bg-amber-600';
    return 'bg-emerald-500 hover:bg-emerald-600';
  };

  const getPriorityBorderColor = (priority) => {
    if (priority >= 8) return 'border-l-red-500';
    if (priority >= 5) return 'border-l-amber-500';
    return 'border-l-emerald-500';
  };

  const getPriorityBgColor = (priority) => {
    if (priority >= 8) return 'bg-red-50';
    if (priority >= 5) return 'bg-amber-50';
    return 'bg-emerald-50';
  };

  // Scroll to 6:00 vertically and to today horizontally (today at the LEFT)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Vertical scroll to 6:00
      if (timeGridRef.current) {
        timeGridRef.current.scrollTop = 6 * HOUR_HEIGHT;
      }
      
      // Horizontal scroll - sync both header and body
      const todayIndex = weekDates.findIndex(d => formatDateKey(d) === todayStr);
      if (todayIndex > 0) {
        const columnWidth = 90;
        const scrollAmount = todayIndex * columnWidth;
        // Use requestAnimationFrame for smoother scroll after render
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollAmount;
          }
          if (bodyScrollRef.current) {
            bodyScrollRef.current.scrollLeft = scrollAmount;
          }
        });
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, [currentDate]);

  const handleSaveGoal = () => {
    onWeekGoalChange(weekKey, goalText);
    setEditingGoal(false);
  };

  // Wizard-Ziele: State + Handler
  const WIZARD_KEY = "burningteam_wizard_goals";
  const [wizardData, setWizardData] = useState(() => {
    try { return JSON.parse(userStorage.getItem(WIZARD_KEY)); } catch { return null; }
  });

  useEffect(() => {
    const refresh = () => {
      try { setWizardData(JSON.parse(userStorage.getItem(WIZARD_KEY))); } catch { setWizardData(null); }
    };
    window.addEventListener("burningteam-wizard-sync", refresh);
    return () => window.removeEventListener("burningteam-wizard-sync", refresh);
  }, []);

  // Wizard Fortschritt ändern (mit Kaskadierung)
  const handleWizardProgressChange = (newProgress) => {
    if (!wizardData?.weekGoals?.[weekKey]) return;
    const updated = JSON.parse(JSON.stringify(wizardData));
    const entry = updated.weekGoals[weekKey];
    entry.progress = Math.max(0, Math.min(entry.number, newProgress));
    entry.done = entry.progress >= entry.number;
    const monthKey = entry.monthKey;
    if (monthKey && updated.monthGoals?.[monthKey]) {
      const weeksInMonth = Object.entries(updated.weekGoals).filter(([, v]) => v.monthKey === monthKey);
      updated.monthGoals[monthKey].done = weeksInMonth.every(([k, v]) => k === weekKey ? entry.done : v.done);
      if (updated.yearGoal) {
        updated.yearGoal.done = Object.values(updated.monthGoals).every(m => m.done);
      }
    }
    userStorage.setItem(WIZARD_KEY, JSON.stringify(updated));
    setWizardData(updated);
    window.dispatchEvent(new CustomEvent("burningteam-wizard-sync"));
  };

  // Wizard Fortschritt-Anzeige (Dots für ≤10, Eingabe für >10)
  const WizardProgress = ({ entry }) => {
    if (!entry) return null;
    const num = Math.round(entry.number);
    const prog = entry.progress || 0;
    if (num <= 10) {
      return (
        <div className="flex items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
          {Array.from({ length: num }, (_, i) => (
            <button key={i} type="button"
              onClick={() => handleWizardProgressChange(i < prog ? i : i + 1)}
              className={`w-4 h-4 rounded-full border-2 transition-all touch-manipulation ${i < prog ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400 bg-white'}`}
              title={`${i + 1} von ${num}`} />
          ))}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
        <button type="button" onClick={() => handleWizardProgressChange(prog - 1)}
          className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center touch-manipulation">−</button>
        <input type="number" value={prog}
          onChange={e => handleWizardProgressChange(parseInt(e.target.value) || 0)}
          min="0" max={num}
          className="w-10 text-center text-xs border border-slate-200 rounded px-0.5 py-0.5 font-medium" />
        <span className="text-xs text-slate-400">/{num}</span>
        <button type="button" onClick={() => handleWizardProgressChange(prog + 1)}
          className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center touch-manipulation">+</button>
        {prog >= num && <span className="text-emerald-600 text-xs font-bold">✓</span>}
      </div>
    );
  };

  // Wizard-Ziel Zeile: Mobile 2-zeilig (Dots zentriert), Desktop inline
  const WizardGoalRow = () => {
    const entry = wizardData?.weekGoals?.[weekKey];
    return (
      <div className="flex-1 min-w-0 w-full">
        {/* Zeile 1: Icon + Label + Text */}
        <div className="flex items-center gap-1.5 min-w-0">
          <Target className="w-4 h-4 text-emerald-600 shrink-0" />
          <span className="text-sm font-medium text-slate-700 shrink-0">Wochenziel:</span>
          {entry ? (
            <span className={`text-sm truncate min-w-0 ${entry.done ? 'line-through text-slate-400' : 'text-slate-600'}`}>
              {entry.number} {entry.text}
            </span>
          ) : (
            <span className="text-sm text-slate-400 italic">Nicht geplant</span>
          )}
          {/* Desktop: Dots inline nach dem Text */}
          {entry && <div className="hidden sm:block shrink-0 ml-1"><WizardProgress entry={entry} /></div>}
        </div>
        {/* Mobile only: Dots zentriert auf Zeile 2 */}
        {entry && (
          <div className="sm:hidden relative h-6 mt-1">
            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0 }}>
              <WizardProgress entry={entry} />
            </div>
          </div>
        )}
      </div>
    );
  };

  const formatDateKey = (date) => {
    // Use local time, not UTC (toISOString uses UTC which shifts dates!)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleFocusEdit = (dateKey) => {
    setEditingFocus(dateKey);
    setFocusText(dayFocus[dateKey] || '');
  };

  const handleFocusSave = (dateKey) => {
    onDayFocusChange(dateKey, focusText);
    setEditingFocus(null);
  };

  const handleTimeSlotClick = (date, hour, minutes = 0) => {
    const dateStr = formatDateKey(date);
    const timeStr = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setSelectedTime(timeStr);
    setEditingAppointment(null);
    setShowModal(true);
  };
  
  // Helper: check if a date is in the past
  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  const handleSaveAppointment = async (appointment) => {
    const currentAppointments = appointments || [];
    const oldAppt = currentAppointments.find(a => a.id === appointment.id);
    const existingIndex = currentAppointments.findIndex(a => a.id === appointment.id);
    
    if (existingIndex >= 0) {
      const updated = [...currentAppointments];
      updated[existingIndex] = appointment;
      onAppointmentChange(updated);
    } else {
      onAppointmentChange([...currentAppointments, appointment]);
    }

    // Fire transfer-complete event so TodosView deletes the source todo
    if (pendingTransferTodo) {
      window.dispatchEvent(new CustomEvent('burningteam-transfer-complete', { detail: pendingTransferTodo }));
      setPendingTransferTodo(null);
    }

    // ── Kalender → Kontakt Sync ──────────────────────────────────────────
    // Nur bei direkter Appointment-Kontakt-Verknüpfung (keine Task-Links)
    if (appointment.contactRef?.id && !appointment.contactRef.taskId) {
      const updateData = {};
      // Titel → naechstes_thema
      if (appointment.title !== (oldAppt?.title ?? '')) {
        updateData.naechstes_thema = appointment.title || '';
      }
      // Datum + Uhrzeit → naechster_termin
      if (appointment.date !== oldAppt?.date || appointment.startTime !== oldAppt?.startTime) {
        updateData.naechster_termin = `${appointment.date}T${appointment.startTime}:00`;
      }
      // Notiz → notizen
      if ((appointment.note || '') !== (oldAppt?.note || '')) {
        updateData.notizen = appointment.note || '';
      }
      if (Object.keys(updateData).length > 0) {
        try {
          const typ = appointment.contactRef.typ;
          const url = typ === 'ich'
            ? `${API}/api/auth/me/profile`
            : `${API}/api/teampartner/${appointment.contactRef.id}`;
          await axios.put(url, updateData);
        } catch { /* Sync-Fehler ignorieren */ }
      }
    }
  };

  const handleDeleteAppointment = (appointmentId) => {
    const currentAppointments = appointments || [];
    const aptToDelete = currentAppointments.find(a => a.id === appointmentId);
    
    // Delete from all views if the function is available
    if (onDeleteFromAll && aptToDelete) {
      onDeleteFromAll(appointmentId, aptToDelete.date);
    } else {
      // Fallback: just delete from week view
      onAppointmentChange(currentAppointments.filter(a => a.id !== appointmentId));
    }
  };

  const handleAppointmentClick = (appointment, e) => {
    e.stopPropagation();
    setEditingAppointment(appointment);
    setSelectedDate(appointment.date);
    setSelectedTime(appointment.startTime);
    setShowModal(true);
  };

  const getAppointmentsForDate = (dateStr) => {
    // Return single-day appointments for this date
    return (appointments || []).filter(a => a.date === dateStr && !a.isMultiDay);
  };
  
  // Get multi-day appointments that span this date
  const getMultiDayAppointmentsForDate = (dateStr) => {
    return (appointments || []).filter(a => {
      if (!a.isMultiDay || !a.endDate) return false;
      return dateStr >= a.date && dateStr <= a.endDate;
    });
  };

  // Get all multi-day appointments for consistent color indexing
  const getAllMultiDayAppointments = () => {
    return (appointments || []).filter(a => a.isMultiDay && a.endDate).sort((a, b) => (a.id || 0) - (b.id || 0));
  };

  // Get color index for a multi-day appointment (consistent across all views)
  const getWeekMultiDayColorIndex = (appointmentId) => {
    const allMultiDay = getAllMultiDayAppointments();
    const idx = allMultiDay.findIndex(a => a.id === appointmentId);
    return idx >= 0 ? idx : 0;
  };
  
  // Determine what type of rendering to use for a multi-day appointment on a specific date
  // Returns: 'start' (first day), 'middle' (continuation), 'end' (last day), or 'single' (only day in view)
  const getMultiDayPosition = (appointment, dateStr) => {
    if (!appointment.isMultiDay || !appointment.endDate) return 'single';
    
    const isStartDay = dateStr === appointment.date;
    const isEndDay = dateStr === appointment.endDate;
    
    if (isStartDay && isEndDay) return 'single';
    if (isStartDay) return 'start';
    if (isEndDay) return 'end';
    return 'middle';
  };

  // Check if a new appointment overlaps with existing ones
  // Returns the overlapping appointment or null if no overlap
  // Note: Overlapping SHORT appointments are NOT allowed
  // But SHORT can overlap with LONG (multi-day) appointments
  const checkOverlapForDate = (date, startTime, endTime, excludeId = null, isMultiDay = false) => {
    // Multi-day appointments can always overlap with single-day
    if (isMultiDay) return null;
    
    // Only check single-day appointments for overlap with other single-day
    const dayAppointments = (appointments || []).filter(a => 
      a.date === date && 
      a.id !== excludeId && 
      !a.isMultiDay && 
      !(a.endDate && a.endDate !== a.date) // exclude multi-day
    );
    
    for (const apt of dayAppointments) {
      const [newStartH, newStartM] = startTime.split(':').map(Number);
      const [newEndH, newEndM] = endTime.split(':').map(Number);
      const [aptStartH, aptStartM] = apt.startTime.split(':').map(Number);
      const [aptEndH, aptEndM] = apt.endTime.split(':').map(Number);
      
      const newStart = newStartH * 60 + newStartM;
      const newEnd = newEndH * 60 + newEndM;
      const aptStart = aptStartH * 60 + aptStartM;
      const aptEnd = aptEndH * 60 + aptEndM;
      
      // Check for real overlap (not just adjacent)
      if (newStart < aptEnd && newEnd > aptStart) {
        return apt;
      }
    }
    return null;
  };

  // Calculate appointment position relative to hour grid
  const getAppointmentStyle = (appointment) => {
    const [startHour, startMin] = appointment.startTime.split(':').map(Number);
    const [endHour, endMin] = appointment.endTime.split(':').map(Number);
    
    const top = startHour * HOUR_HEIGHT + (startMin / 60) * HOUR_HEIGHT;
    const endPos = endHour * HOUR_HEIGHT + (endMin / 60) * HOUR_HEIGHT;
    // 15-min events should be exactly HOUR_HEIGHT / 4 = 16px
    const minHeight = HOUR_HEIGHT / 4;
    const rawHeight = endPos - top;
    // Add 1px margin for visual separation between stacked appointments
    const height = Math.max(rawHeight - 1, minHeight);
    
    return { top: `${top}px`, height: `${height}px` };
  };

  // Check if two appointments overlap in time
  const appointmentsOverlap = (apt1, apt2) => {
    const [start1Hour, start1Min] = apt1.startTime.split(':').map(Number);
    const [end1Hour, end1Min] = apt1.endTime.split(':').map(Number);
    const [start2Hour, start2Min] = apt2.startTime.split(':').map(Number);
    const [end2Hour, end2Min] = apt2.endTime.split(':').map(Number);
    
    const start1 = start1Hour * 60 + start1Min;
    const end1 = end1Hour * 60 + end1Min;
    const start2 = start2Hour * 60 + start2Min;
    const end2 = end2Hour * 60 + end2Min;
    
    // Two appointments overlap if one starts before the other ends
    return start1 < end2 && start2 < end1;
  };

  // Calculate position for overlapping appointments (single-day AND multi-day)
  const getAppointmentStyleWithOverlap = (appointment, allDayAppointments, multiDayAppointments = []) => {
    const baseStyle = getAppointmentStyle(appointment);
    
    // Parse this appointment's time
    const [thisStartH, thisStartM] = appointment.startTime.split(':').map(Number);
    const [thisEndH, thisEndM] = appointment.endTime.split(':').map(Number);
    const thisStart = thisStartH * 60 + thisStartM;
    const thisEnd = thisEndH * 60 + thisEndM;
    
    // Combine single-day and multi-day appointments for overlap detection
    const allAppointments = [...allDayAppointments, ...multiDayAppointments.map(apt => ({
      ...apt,
      isMultiDay: true
    }))];
    
    // Find all appointments that overlap in time with this one
    const overlapping = allAppointments.filter(apt => {
      if (apt.id === appointment.id) return false;
      
      const [aptStartH, aptStartM] = (apt.startTime || '09:00').split(':').map(Number);
      const aptStart = aptStartH * 60 + aptStartM;
      
      // For multi-day appointments, use their start block (30 min)
      let aptEnd;
      if (apt.isMultiDay) {
        aptEnd = aptStart + 30; // Multi-day start block is 30min
      } else {
        const [aptEndH, aptEndM] = (apt.endTime || '10:00').split(':').map(Number);
        aptEnd = aptEndH * 60 + aptEndM;
      }
      
      // Check time overlap
      return thisStart < aptEnd && aptStart < thisEnd;
    });
    
    if (overlapping.length === 0) {
      // No overlaps - full width
      return { ...baseStyle, left: '0', right: '2px', width: 'auto' };
    }
    
    // Sort all overlapping appointments: Single-day (short) left, Multi-day (long) right
    const group = [appointment, ...overlapping].sort((a, b) => {
      // Single-day first (left), Multi-day last (right)
      if (a.isMultiDay !== b.isMultiDay) return a.isMultiDay ? 1 : -1;
      const [aH, aM] = (a.startTime || '09:00').split(':').map(Number);
      const [bH, bM] = (b.startTime || '09:00').split(':').map(Number);
      const timeDiff = (aH * 60 + aM) - (bH * 60 + bM);
      return timeDiff !== 0 ? timeDiff : String(a.id).localeCompare(String(b.id));
    });
    
    // Find this appointment's position in the group
    const index = group.findIndex(apt => apt.id === appointment.id);
    const total = group.length;
    
    // Calculate width: each appointment gets equal share, directly adjacent
    const widthPercent = 100 / total;
    const leftPercent = index * widthPercent;
    
    return { 
      ...baseStyle, 
      left: `${leftPercent}%`, 
      width: `${widthPercent}%`,
      right: 'auto'
    };
  };
  
  // Get style for multi-day appointment based on position
  const getMultiDayStyle = (appointment, position) => {
    const [startHour, startMin] = appointment.startTime.split(':').map(Number);
    const [endHour, endMin] = appointment.endTime.split(':').map(Number);
    
    if (position === 'start') {
      // First day: Small block (1 hour max) at start time
      const top = startHour * HOUR_HEIGHT + (startMin / 60) * HOUR_HEIGHT;
      const blockHeight = Math.min(HOUR_HEIGHT, HOUR_HEIGHT); // 1 hour block
      return { top: `${top}px`, height: `${blockHeight}px` };
    } else if (position === 'end') {
      // Last day: Small block (15min) ending at end time
      const endPos = endHour * HOUR_HEIGHT + (endMin / 60) * HOUR_HEIGHT;
      const blockHeight = HOUR_HEIGHT / 4; // 15 min block
      const top = endPos - blockHeight;
      return { top: `${Math.max(0, top)}px`, height: `${blockHeight}px` };
    } else if (position === 'middle') {
      // Middle days: handled separately with thin line
      return { top: '0px', height: '100%' };
    }
    // Single day - use normal style
    return getAppointmentStyle(appointment);
  };

  // Check if appointment is longer than 15 minutes
  const isLongerThan15Min = (appointment) => {
    const [startHour, startMin] = appointment.startTime.split(':').map(Number);
    const [endHour, endMin] = appointment.endTime.split(':').map(Number);
    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;
    return (endTotal - startTotal) > 15;
  };

  // Check if appointment is long enough to show notes (more than 45 minutes)
  const hasSpaceForNotes = (appointment) => {
    const [startHour, startMin] = appointment.startTime.split(':').map(Number);
    const [endHour, endMin] = appointment.endTime.split(':').map(Number);
    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;
    return (endTotal - startTotal) >= 45;
  };

  // List Mode View for Week
  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {/* Kompakter Header: Wochenziel + Wochen-Aufgaben */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
            {/* Wochenziel - einzeilig, nicht editierbar */}
            <WizardGoalRow />

            {/* Divider */}
            <div className="hidden sm:block w-px h-6 bg-slate-200 shrink-0"></div>

            <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end sm:gap-1">
            {/* Wochen-Aufgaben */}
            <div 
              onClick={() => setShowWeekTodosOverview(true)}
              className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-purple-50 rounded-lg px-1 py-0.5 sm:px-2 sm:py-1 transition-colors"
            >
              <ListTodo className="w-4 h-4 text-purple-600 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Aufgaben:</span>
              {(() => {
                const grouped = getWeekTodosGrouped();
                let total = 0, done = 0;
                Object.values(grouped).forEach(todos => {
                  total += todos.length;
                  done += todos.filter(t => t.done).length;
                });
                return (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{total - done} offen</span>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{done} ✓</span>
                  </div>
                );
              })()}
            </div>
            
            {/* Neuer Termin Button - nach rechts verschoben */}
            <button 
              onClick={() => { setSelectedDate(formatDateKey(new Date())); setSelectedTime('09:00'); setEditingAppointment(null); setShowModal(true); }}
              className="w-7 h-7 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded transition-colors flex items-center justify-center"
              title="Neuer Termin"
              data-testid="week-list-new-appointment-btn"
            >
              <Plus className="w-4 h-4" />
            </button>
            </div>
          </div>
        </div>

        {/* Wochenziel Edit Modal */}
        {editingGoal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-600" />
                  Wochenziel - KW {weekNumber}
                </h3>
                <button onClick={() => setEditingGoal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <textarea 
                value={goalText} 
                onChange={(e) => setGoalText(e.target.value)} 
                placeholder="Was möchtest du diese Woche erreichen?" 
                className="w-full p-3 border border-slate-200 rounded-lg text-sm" 
                rows={4} 
                autoFocus 
              />
              <div className="flex gap-2 mt-4">
                <Button onClick={handleSaveGoal} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Speichern</Button>
                <Button onClick={() => setEditingGoal(false)} variant="outline">Abbrechen</Button>
              </div>
            </div>
          </div>
        )}

        {/* Days as List */}
        {weekDates.map((date, idx) => {
          const dateKey = formatDateKey(date);
          const dayAppts = getAppointmentsForDate(dateKey);
          const multiDayAppts = getMultiDayAppointmentsForDate(dateKey);
          const allAppts = [...dayAppts, ...multiDayAppts];
          const isToday = dateKey === todayStr;
          
          return (
            <div key={dateKey} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isToday ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-slate-200'}`}>
              <div className={`flex items-center justify-between px-4 py-2 border-b ${isToday ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                <h3 className={`font-semibold ${isToday ? 'text-emerald-700' : 'text-slate-800'}`}>
                  {DAYS_SHORT[idx]} {date.getDate()}.{date.getMonth() + 1}.
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{allAppts.length} Termine</span>
                  {/* Action buttons - same style as month list view */}
                  <div className="flex gap-1">
                    {/* Tages-Aufgaben Button (purple) */}
                    <button
                      onClick={() => openDayTodosModal(dateKey)}
                      className="w-7 h-7 text-sm font-bold text-white bg-purple-500 hover:bg-purple-600 rounded transition-colors flex items-center justify-center relative"
                      title="Tages-Aufgaben"
                    >
                      <ListTodo className="w-4 h-4" />
                      {getDayTodos(dateKey).filter(t => !t.done).length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                          {getDayTodos(dateKey).filter(t => !t.done).length}
                        </span>
                      )}
                    </button>
                    {/* Neuer Termin Button (amber) */}
                    <button
                      onClick={() => { setSelectedDate(dateKey); setSelectedTime('09:00'); setEditingAppointment(null); setShowModal(true); }}
                      className="w-7 h-7 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded transition-colors flex items-center justify-center"
                      title="Neuer Termin"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              {allAppts.length === 0 ? (
                <div className="p-3 text-center text-slate-400 text-sm">Keine Termine</div>
              ) : (
                <div className="space-y-1 p-1">
                  {allAppts.map((apt, aptIdx) => {
                    const priority = apt.priority || 5;
                    const isMultiDay = apt.isMultiDay && apt.endDate;
                    const position = isMultiDay ? getMultiDayPosition(apt, dateKey) : null;
                    const priorityBorderColor = priority >= 8 ? 'border-l-red-500' : 
                      priority >= 5 ? 'border-l-amber-500' : 'border-l-emerald-500';
                    
                    // Get consistent color based on appointment - use index for multi-day
                    const colorIndex = isMultiDay ? getWeekMultiDayColorIndex(apt.id) : 0;
                    const aptColor = getAppointmentColor(apt, colorIndex);
                    
                    return (
                      <div
                        key={apt.id || aptIdx}
                        onClick={(e) => handleAppointmentClick(apt, e)}
                        className={`p-2 cursor-pointer hover:opacity-80 border-l-4 ${aptColor.light} rounded-r shadow-sm ${priorityBorderColor}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium ${aptColor.lightText} flex items-center gap-2`}>
                              {apt.title}
                              {isMultiDay && (
                                <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                  {position === 'start' ? 'Start' : position === 'end' ? 'Ende' : 'Fortsetzung'}
                                </span>
                              )}
                            </div>
                            {isMultiDay && (
                              <div className="text-xs text-slate-500 mt-0.5">
                                {apt.date?.split('-').reverse().slice(0,2).join('.')} - {apt.endDate?.split('-').reverse().slice(0,2).join('.')}
                              </div>
                            )}
                          </div>
                          <div className="text-sm font-medium text-slate-600 whitespace-nowrap">
                            {apt.startTime} - {apt.endTime}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        
        {/* Appointment Modal */}
        <AppointmentModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditingAppointment(null); }}
          onSave={handleSaveAppointment}
          onDelete={handleDeleteAppointment}
          initialData={editingAppointment}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          checkOverlap={checkOverlapForDate}
          calendars={googleCalendars}
          primaryCalendar={primaryCalendar}
          onToggleDone={onToggleAppointmentDone}
          isToday={editingAppointment?.date === getLocalDateStr()}
        />

        {/* Week Todos Overview Modal */}
        {showWeekTodosOverview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-4 max-w-5xl w-full shadow-xl max-h-[85vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <ListTodo className="w-5 h-5 text-purple-600" />
                  Wochen-Aufgaben - KW {weekNumber}
                </h3>
                <button onClick={() => setShowWeekTodosOverview(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {(() => {
                    const grouped = getWeekTodosGrouped();
                    const now = new Date();
                    const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1;
                    
                    const tiles = [
                      ...DAYS_SHORT.map((dayName, dayIndex) => ({ 
                        key: dayIndex, 
                        name: dayName, 
                        isDay: true,
                        isToday: dayIndex === todayWeekday 
                      })),
                      { key: 'unassigned', name: 'Diese Woche', isDay: false, isToday: false }
                    ];
                    
                    return tiles.map((tile) => {
                      const todos = grouped[tile.key] || [];
                      const openTodos = todos.filter(t => !t.done).length;
                      const doneTodos = todos.filter(t => t.done).length;
                      
                      return (
                        <div 
                          key={tile.key}
                          className={`rounded-xl border-2 p-2 min-w-0 ${
                            tile.isToday 
                              ? 'border-emerald-400 bg-emerald-50' 
                              : tile.key === 'unassigned'
                                ? 'border-purple-300 bg-purple-50'
                                : 'border-slate-200 bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className={`text-xs font-bold ${
                                tile.isToday ? 'text-emerald-700' : 
                                tile.key === 'unassigned' ? 'text-purple-700' : 'text-slate-700'
                              }`}>
                                {tile.name}
                              </span>
                              {tile.isToday && (
                                <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">
                                  Heute
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-[10px]">
                              {openTodos > 0 && (
                                <span className={`px-1.5 py-0.5 rounded-full ${
                                  tile.key === 'unassigned' ? 'bg-purple-200 text-purple-700' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {openTodos}
                                </span>
                              )}
                              {doneTodos > 0 && (
                                <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                                  ✓{doneTodos}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="space-y-1 mb-2 max-h-[150px] overflow-y-auto">
                            {todos.length === 0 ? (
                              <p className="text-slate-400 text-[10px] text-center py-2">Keine Aufgaben</p>
                            ) : (
                              todos.map(todo => (
                                <div
                                  key={todo.id}
                                  className={`p-1.5 rounded-lg flex items-center gap-1.5 text-xs transition-colors ${
                                    todo.done 
                                      ? 'bg-white/50 text-slate-400' 
                                      : 'bg-white text-slate-700 hover:bg-purple-50'
                                  }`}
                                >
                                  <button
                                    onClick={() => toggleWeekTodo(todo.id, todo.source, tile.key)}
                                    className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                      todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-purple-400'
                                    }`}
                                  >
                                    {todo.done && <Check className="w-2 h-2 text-white" />}
                                  </button>
                                  <button
                                    onClick={() => onEditTodo && onEditTodo(todo, 'weekly', { weekKey: tile.key })}
                                    className={`flex-1 truncate text-left text-xs ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}
                                  >{todo.text}</button>
                                  <button
                                    onClick={() => deleteWeekTodo(todo.id, todo.source)}
                                    className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>

                          <div className="flex gap-1">
                            {(tile.key === 'unassigned' || !isDateInPast(weekDates[tile.key])) ? (
                            <>
                            <input
                              type="text"
                              value={selectedWeekday === tile.key ? newWeekTodoText : ''}
                              onChange={(e) => {
                                setSelectedWeekday(tile.key);
                                setNewWeekTodoText(e.target.value);
                              }}
                              onFocus={() => setSelectedWeekday(tile.key)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  setSelectedWeekday(tile.key);
                                  addWeekTodo(tile.key);
                                }
                              }}
                              placeholder="+ Neu..."
                              className="flex-1 min-w-0 p-1.5 border border-slate-200 rounded text-[10px] bg-white"
                            />
                            <button 
                              onClick={() => {
                                setSelectedWeekday(tile.key);
                                addWeekTodo(tile.key);
                              }}
                              className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded flex-shrink-0"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                            </>
                            ) : (
                            <div className="text-[10px] text-slate-400 text-center py-1 w-full">Vergangen</div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-slate-200 mt-4">
                <div className="text-sm text-slate-500">
                  {(() => {
                    const grouped = getWeekTodosGrouped();
                    let total = 0, done = 0;
                    Object.values(grouped).forEach(todos => {
                      total += todos.length;
                      done += todos.filter(t => t.done).length;
                    });
                    return `${done}/${total} Aufgaben erledigt`;
                  })()}
                </div>
                <Button onClick={() => setShowWeekTodosOverview(false)} variant="outline">
                  Schließen
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Day Todos Modal for List View */}
        {showDayTodosModal && dayTodosDate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <ListTodo className="w-5 h-5 text-purple-600" />
                  Aufgaben - {(() => {
                    const d = new Date(dayTodosDate);
                    const dayName = DAYS_SHORT[d.getDay() === 0 ? 6 : d.getDay() - 1];
                    return `${dayName}, ${d.getDate()}.${d.getMonth() + 1}.`;
                  })()}
                </h3>
                <button onClick={() => setShowDayTodosModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Todo List */}
              <div className="flex-1 overflow-y-auto space-y-2 mb-4">
                {getDayTodos(dayTodosDate).length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">Keine Aufgaben für diesen Tag</p>
                ) : (
                  getDayTodos(dayTodosDate).map(todo => (
                    <div key={todo.id} className={`p-3 rounded-lg flex items-center gap-3 transition-colors ${todo.done ? 'bg-slate-100' : 'bg-purple-50 hover:bg-purple-100'}`}>
                      <button
                        onClick={() => toggleDayTodo(todo.id, todo.source)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400'}`}
                      >
                        {todo.done && <Check className="w-3 h-3 text-white" />}
                      </button>
                      <button
                        onClick={() => onEditTodo && onEditTodo(todo, 'daily', {})}
                        className={`flex-1 text-left text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}
                      >{todo.text}</button>
                    </div>
                  ))
                )}
              </div>

              {/* Add New Todo - only for today or future */}
              {dayTodosDate && !isDateInPast(new Date(dayTodosDate)) ? (
              <div className="flex gap-2 pt-3 border-t border-slate-200">
                <input
                  type="text"
                  value={newDayTodoText}
                  onChange={(e) => setNewDayTodoText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addDayTodo()}
                  placeholder="Neue Aufgabe..."
                  className="flex-1 p-3 border border-slate-200 rounded-lg text-sm"
                  autoFocus
                />
                <Button onClick={addDayTodo} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              ) : (
              <div className="text-xs text-slate-400 text-center py-3 border-t border-slate-200">Vergangener Tag – nicht mehr bearbeitbar</div>
              )}
              
              {/* Button to show full week */}
              <button 
                onClick={() => { setShowDayTodosModal(false); setShowWeekTodosOverview(true); }} 
                className="w-full mt-3 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
              >
                Ganze Woche anzeigen →
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Kompakter Header: Wochenziel + Wochen-Aufgaben (Kalender-Modus) */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
          {/* Wochenziel - einzeilig, nicht editierbar */}
          <WizardGoalRow />

          {/* Divider */}
          <div className="hidden sm:block w-px h-6 bg-slate-200 shrink-0"></div>

          <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end sm:gap-1">
          {/* Wochen-Aufgaben */}
          <div 
            onClick={() => setShowWeekTodosOverview(true)}
            className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-purple-50 rounded-lg px-1 py-0.5 sm:px-2 sm:py-1 transition-colors"
          >
            <ListTodo className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Aufgaben:</span>
            {(() => {
              const grouped = getWeekTodosGrouped();
              let total = 0, done = 0;
              Object.values(grouped).forEach(todos => {
                total += todos.length;
                done += todos.filter(t => t.done).length;
              });
              return (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{total - done} offen</span>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{done} ✓</span>
                </div>
              );
            })()}
          </div>
          
          {/* Neuer Termin Button - ganz rechts */}
          <button 
            onClick={() => { setSelectedDate(formatDateKey(new Date())); setSelectedTime('09:00'); setEditingAppointment(null); setShowModal(true); }}
            className="w-7 h-7 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded transition-colors flex items-center justify-center"
            title="Neuer Termin"
          >
            <Plus className="w-4 h-4" />
          </button>
          </div>
        </div>
      </div>

      {/* Wochenziel Edit Modal */}
      {editingGoal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Wochenziel - KW {weekNumber}
              </h3>
              <button onClick={() => setEditingGoal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea 
              value={goalText} 
              onChange={(e) => setGoalText(e.target.value)} 
              placeholder="Was möchtest du diese Woche erreichen?" 
              className="w-full p-3 border border-slate-200 rounded-lg text-sm" 
              rows={4} 
              autoFocus 
            />
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSaveGoal} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Speichern</Button>
              <Button onClick={() => setEditingGoal(false)} variant="outline">Abbrechen</Button>
            </div>
          </div>
        </div>
      )}

      {/* Week Calendar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header Row - Zeit + Tage */}
        <div className="flex border-b border-slate-200">
          {/* Zeit Header */}
          <div className="w-16 flex-shrink-0 bg-slate-50 border-r border-slate-200 p-2 text-center text-xs font-medium text-slate-500">
            Zeit
          </div>
          {/* Days Header - horizontal scrollable */}
          <div 
            ref={scrollContainerRef} 
            className="flex-1 overflow-x-auto"
            onScroll={(e) => {
              // Sync body scroll
              if (bodyScrollRef.current) {
                bodyScrollRef.current.scrollLeft = e.target.scrollLeft;
              }
            }}
          >
            <div className="flex min-w-[630px]">
              {weekDates.map((date, index) => {
                const dateKey = formatDateKey(date);
                const isToday = dateKey === todayStr;
                const focusValue = dayFocus[dateKey];
                return (
                  <div 
                    key={index}
                    className={`flex-1 min-w-[90px] p-2 text-center border-r border-slate-100 last:border-r-0 ${isToday ? 'bg-emerald-50' : ''}`}
                  >
                    <div className={`text-xs font-bold ${isToday ? 'text-emerald-600' : 'text-slate-800'}`}>
                      {DAYS_SHORT[index]}
                    </div>
                    <div className={`text-base font-semibold ${isToday ? 'text-emerald-600' : 'text-slate-700'}`}>
                      {date.getDate()}.{date.getMonth() + 1}
                    </div>
                    
                    {editingFocus === dateKey ? (
                      <textarea
                        value={focusText}
                        onChange={(e) => setFocusText(e.target.value)}
                        onBlur={() => handleFocusSave(dateKey)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleFocusSave(dateKey);
                          }
                        }}
                        placeholder="Fokus..."
                        className="w-full mt-1 px-1 py-0.5 text-xs border border-emerald-300 rounded resize-none"
                        rows={2}
                        autoFocus
                      />
                    ) : (
                      <div
                        onClick={() => handleFocusEdit(dateKey)}
                        className={`mt-1 w-full text-xs px-1 py-0.5 rounded text-left cursor-pointer ${
                          focusValue ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400 hover:bg-emerald-50'
                        }`}
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                          hyphens: 'auto',
                          WebkitHyphens: 'auto',
                          MozHyphens: 'auto',
                          msHyphens: 'auto',
                          minHeight: '32px',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.3'
                        }}
                        lang="de"
                      >
                        {focusValue || '+ Fokus'}
                      </div>
                    )}
                    
                    {/* Aufgaben Button */}
                    <button
                      onClick={() => openDayTodosModal(dateKey)}
                      className={`mt-1 w-full text-xs px-1 py-1 rounded flex items-center justify-center gap-1 ${
                        getDayTodos(dateKey).filter(t => !t.done).length > 0 
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                          : 'bg-slate-100 text-slate-500 hover:bg-purple-50'
                      }`}
                    >
                      <ListTodo className="w-3 h-3" />
                      {getDayTodos(dateKey).filter(t => !t.done).length > 0 
                        ? `${getDayTodos(dateKey).filter(t => !t.done).length}` 
                        : '+'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Time Grid Body - vertical scroll for both Zeit and Days together */}
        <div ref={timeGridRef} className="max-h-[500px] overflow-y-auto">
          <div className="flex">
            {/* Zeit Column */}
            <div className="w-16 flex-shrink-0 bg-slate-50 border-r border-slate-200">
              {HOURS.map(hour => (
                <div key={hour} style={{ height: `${HOUR_HEIGHT}px` }} className="relative border-b border-slate-100">
                  <span className="absolute top-0 left-1 text-xs text-slate-500 font-medium">{hour.toString().padStart(2, '0')}:00</span>
                  <span className="absolute text-[10px] text-slate-400 left-1" style={{ top: `${HOUR_HEIGHT / 2 - 6}px` }}>{hour.toString().padStart(2, '0')}:30</span>
                </div>
              ))}
            </div>
            
            {/* Days Grid - synced horizontal scroll with header */}
            <div 
              ref={bodyScrollRef}
              className="flex-1 overflow-x-auto"
              onScroll={(e) => {
                // Sync header scroll
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollLeft = e.target.scrollLeft;
                }
              }}
            >
              <div className="flex min-w-[630px]" style={{ height: `${24 * HOUR_HEIGHT}px` }}>
                {weekDates.map((date, dayIndex) => {
                  const dateKey = formatDateKey(date);
                  const isToday = dateKey === todayStr;
                  const dayAppointments = getAppointmentsForDate(dateKey);
                  
                  return (
                    <div 
                      key={dayIndex}
                      className={`flex-1 min-w-[90px] border-r border-slate-100 last:border-r-0 relative ${isToday ? 'bg-emerald-50/30' : ''}`}
                    >
                      {HOURS.map(hour => (
                        <div
                          key={hour}
                          onClick={() => handleTimeSlotClick(date, hour, 0)}
                          className="absolute w-full border-b border-slate-200 hover:bg-emerald-50/50 cursor-pointer transition-colors"
                          style={{ top: `${hour * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                        >
                          {/* 30min dashed line */}
                          <div className="absolute w-full border-b border-slate-100 border-dashed" style={{ top: `${HOUR_HEIGHT / 2}px` }} />
                        </div>
                      ))}
                      
                      {/* Single-day appointments - with overlap handling */}
                      {dayAppointments.map((appointment, aptIndex) => {
                        const prio = appointment.priority || 5;
                        const priorityBorderColor = prio >= 8 ? 'border-l-red-500' : 
                          prio >= 5 ? 'border-l-amber-500' : 'border-l-emerald-500';
                        const aptColor = getAppointmentColor(appointment);
                        // Calculate style with overlap handling (include multi-day for side-by-side)
                        const multiDayAppts = getMultiDayAppointmentsForDate(dateKey);
                        const style = getAppointmentStyleWithOverlap(appointment, dayAppointments, multiDayAppts);
                        const showNotes = hasSpaceForNotes(appointment) && appointment.note;
                        return (
                          <div
                            key={appointment.id}
                            onClick={(e) => handleAppointmentClick(appointment, e)}
                            style={{...style, boxSizing: 'border-box'}}
                            className={`absolute ${aptColor.bg} ${aptColor.bgHover} ${aptColor.text} rounded-r px-1 text-xs cursor-pointer shadow-sm z-5 overflow-hidden flex flex-col border-l-4 ${priorityBorderColor} border border-slate-300/40`}
                            data-tooltip={`${appointment.title} (${appointment.startTime} - ${appointment.endTime}) P${prio}${appointment.note ? ' - ' + appointment.note : ''}`}
                          >
                            <div className="font-semibold leading-tight break-words pt-1 text-[11px]">
                              {((appointment.isHabitReminder && appointment.completed) || appointment.done) && (
                                <span className="mr-0.5 text-emerald-400">✓</span>
                              )}
                              <span className={(appointment.isHabitReminder && appointment.completed) || appointment.done ? 'line-through opacity-60' : ''}>
                                {appointment.title}
                              </span>
                            </div>
                            {showNotes && (
                              <div className="opacity-70 text-[9px] leading-tight mt-0.5 line-clamp-2 break-words">{appointment.note}</div>
                            )}
                            {isLongerThan15Min(appointment) && (
                              <div className="opacity-80 text-[9px] leading-tight mt-auto pb-0.5">{appointment.startTime} - {appointment.endTime}</div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Multi-day appointments */}
                      {(() => {
                        const multiDayAppts = getMultiDayAppointmentsForDate(dateKey);
                        
                        return multiDayAppts.map((appointment, aptIndex) => {
                          const position = getMultiDayPosition(appointment, dateKey);
                          const [startHour, startMin] = appointment.startTime.split(':').map(Number);
                          const [endHour, endMin] = appointment.endTime.split(':').map(Number);
                          
                          // Priority border color for the stripe
                          const priority = appointment.priority || 5;
                          const priorityBorderColor = priority >= 8 ? 'border-l-red-500' : 
                            priority >= 5 ? 'border-l-amber-500' : 'border-l-emerald-500';
                          
                          // Consistent appointment color based on index in all multi-day appointments
                          const colorIndex = getWeekMultiDayColorIndex(appointment.id);
                          const aptColor = getAppointmentColor(appointment, colorIndex);
                          
                          // Offset lines when multiple events overlap
                          const lineOffset = aptIndex * 6; // 6px offset per event
                          
                          // Format dates for display (e.g., "20.1 - 22.1")
                          const formatShortDate = (dateStr) => {
                            const [year, month, day] = dateStr.split('-');
                            return `${parseInt(day)}.${parseInt(month)}`;
                          };
                          const dateRangeText = `${formatShortDate(appointment.date)} - ${formatShortDate(appointment.endDate)}`;
                          
                          // For middle days: thin line under the block position - use appointment color
                          if (position === 'middle') {
                            return (
                              <div
                                key={`multi-${appointment.id}`}
                                onClick={(e) => handleAppointmentClick(appointment, e)}
                                className={`cursor-pointer z-4 ${aptColor.bg} hover:opacity-80`}
                                style={{ 
                                  position: 'absolute',
                                  top: 0, 
                                  bottom: 0,
                                  width: 4,
                                  right: 2 + lineOffset,
                                  borderRadius: 2
                                }}
                                title={`${appointment.title} (${dateRangeText})`}
                              />
                            );
                          }
                          
                          // For start day: block sized for text + line from block end to midnight
                          if (position === 'start') {
                            const blockTop = startHour * HOUR_HEIGHT + (startMin / 60) * HOUR_HEIGHT;
                            const blockHeight = HOUR_HEIGHT / 2; // ~30min to fit title + date
                            const lineTop = blockTop + blockHeight;
                            
                            // Check for overlapping single-day appointments (time overlap, not just same start)
                            const singleDayAppts = getAppointmentsForDate(dateKey);
                            const multiStart = startHour * 60 + startMin;
                            const multiEnd = multiStart + 30; // Multi-day start block is 30min
                            
                            const overlappingSingle = singleDayAppts.filter(apt => {
                              const [sH, sM] = (apt.startTime || '09:00').split(':').map(Number);
                              const [eH, eM] = (apt.endTime || '10:00').split(':').map(Number);
                              const aptStart = sH * 60 + sM;
                              const aptEnd = eH * 60 + eM;
                              // Check if time ranges overlap
                              return aptStart < multiEnd && multiStart < aptEnd;
                            });
                            
                            // Check for overlapping other multi-day appointments starting on this day
                            const overlappingMulti = multiDayAppts.filter(apt => {
                              if (apt.id === appointment.id) return false;
                              const pos = getMultiDayPosition(apt, dateKey);
                              if (pos !== 'start') return false;
                              const [sH, sM] = (apt.startTime || '09:00').split(':').map(Number);
                              const aptStart = sH * 60 + sM;
                              const aptEnd = aptStart + 30; // Multi-day start block is 30min
                              return aptStart < multiEnd && multiStart < aptEnd;
                            });
                            
                            const allOverlapping = [...overlappingSingle, ...overlappingMulti];
                            let leftOffset = '0';
                            let widthValue = null;
                            
                            if (allOverlapping.length > 0) {
                              // Sort: Single-day (short) first (left), Multi-day (long) last (right)
                              // For multiple multi-day: sort by start time, then by ID for consistency
                              const group = [appointment, ...allOverlapping].sort((a, b) => {
                                const aIsMulti = a.isMultiDay || (a.endDate && a.endDate !== a.date);
                                const bIsMulti = b.isMultiDay || (b.endDate && b.endDate !== b.date);
                                if (aIsMulti !== bIsMulti) return aIsMulti ? 1 : -1;
                                // Then by start time
                                const [aH, aM] = (a.startTime || '09:00').split(':').map(Number);
                                const [bH, bM] = (b.startTime || '09:00').split(':').map(Number);
                                const timeDiff = (aH * 60 + aM) - (bH * 60 + bM);
                                return timeDiff !== 0 ? timeDiff : String(a.id).localeCompare(String(b.id));
                              });
                              const index = group.findIndex(apt => apt.id === appointment.id);
                              const total = group.length;
                              const widthPercent = 100 / total;
                              leftOffset = `${index * widthPercent}%`;
                              widthValue = `${widthPercent}%`;
                            }
                            
                            return (
                              <React.Fragment key={`multi-${appointment.id}`}>
                                {/* Block at start time with title + date range */}
                                <div
                                  onClick={(e) => handleAppointmentClick(appointment, e)}
                                  style={{ 
                                    top: `${blockTop}px`, 
                                    height: `${blockHeight}px`,
                                    left: leftOffset,
                                    boxSizing: 'border-box',
                                    ...(widthValue ? { width: widthValue } : { right: '0.125rem' })
                                  }}
                                  className={`absolute ${aptColor.bg} ${aptColor.bgHover} ${aptColor.text} rounded-r px-1.5 text-xs cursor-pointer shadow-sm z-5 overflow-hidden flex flex-col justify-center border-l-4 ${priorityBorderColor}`}
                                  data-tooltip={`${appointment.title} (${dateRangeText})`}
                                >
                                  <div className="font-semibold leading-tight break-words text-[10px]">
                                    {appointment.title}
                                  </div>
                                  <div className="text-[8px] opacity-80 leading-tight">
                                    {dateRangeText}
                                  </div>
                                </div>
                                {/* Thin line from block end to midnight */}
                                <div
                                  onClick={(e) => handleAppointmentClick(appointment, e)}
                                  className={`cursor-pointer z-4 ${aptColor.bg} hover:opacity-80`}
                                  style={{ 
                                    position: 'absolute',
                                    top: `${lineTop}px`,
                                    bottom: 0,
                                    width: 4,
                                    right: 2 + lineOffset,
                                    borderRadius: 2
                                  }}
                                  title={`${appointment.title} (${dateRangeText})`}
                                />
                              </React.Fragment>
                            );
                          }
                          
                          // For end day: line from midnight to block start + small block at end time
                          if (position === 'end') {
                            const blockEndPos = endHour * HOUR_HEIGHT + (endMin / 60) * HOUR_HEIGHT;
                            const blockHeight = HOUR_HEIGHT / 4; // 15 min block
                            const blockTop = blockEndPos - blockHeight;
                            
                            return (
                              <React.Fragment key={`multi-${appointment.id}`}>
                                {/* Thin line from midnight to block start */}
                                <div
                                  onClick={(e) => handleAppointmentClick(appointment, e)}
                                  className={`cursor-pointer z-4 ${aptColor.bg} hover:opacity-80`}
                                  style={{ 
                                    position: 'absolute',
                                    top: 0,
                                    height: `${Math.max(0, blockTop)}px`,
                                    width: 4,
                                    right: 2 + lineOffset,
                                    borderRadius: 2
                                  }}
                                  title={`${appointment.title} (${dateRangeText})`}
                                />
                                {/* Small block at end time */}
                                <div
                                  onClick={(e) => handleAppointmentClick(appointment, e)}
                                  style={{ top: `${Math.max(0, blockTop)}px`, height: `${blockHeight}px`, boxSizing: 'border-box' }}
                                  className={`absolute left-0 right-0.5 ${aptColor.bg} ${aptColor.bgHover} ${aptColor.text} rounded-r px-1.5 text-xs cursor-pointer shadow-sm z-5 overflow-hidden flex flex-col justify-center border-l-4 ${priorityBorderColor}`}
                                  data-tooltip={`${appointment.title} (${dateRangeText})`}
                                >
                                  <div className="font-semibold leading-tight break-words text-[10px]">
                                    {appointment.title}
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          }
                        
                          // Single day fallback (shouldn't happen for multi-day)
                          return (
                            <div
                              key={`multi-${appointment.id}`}
                              onClick={(e) => handleAppointmentClick(appointment, e)}
                              className={`absolute left-0 right-0.5 ${aptColor.bg} ${aptColor.bgHover} ${aptColor.text} rounded-r px-1.5 text-xs cursor-pointer shadow-sm z-5 overflow-hidden flex flex-col border-l-4 ${priorityBorderColor}`}
                              data-tooltip={`${appointment.title} (${dateRangeText})`}
                            >
                              <div className="font-semibold leading-tight break-words pt-0.5 text-[10px]">
                                {appointment.title}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Day Todos Modal */}
      {showDayTodosModal && dayTodosDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-purple-600" />
                Aufgaben - {(() => {
                  const d = new Date(dayTodosDate);
                  const dayName = DAYS_SHORT[d.getDay() === 0 ? 6 : d.getDay() - 1];
                  return `${dayName}, ${d.getDate()}.${d.getMonth() + 1}.`;
                })()}
              </h3>
              <button onClick={() => setShowDayTodosModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Todo List */}
            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {getDayTodos(dayTodosDate).length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">Keine Aufgaben für diesen Tag</p>
              ) : (
                getDayTodos(dayTodosDate).map(todo => (
                  <div key={todo.id} className={`p-3 rounded-lg flex items-center gap-3 transition-colors ${todo.done ? 'bg-slate-100' : 'bg-purple-50 hover:bg-purple-100'}`}>
                    <button
                      onClick={() => toggleDayTodo(todo.id, todo.source)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400'}`}
                    >
                      {todo.done && <Check className="w-3 h-3 text-white" />}
                    </button>
                    <button
                      onClick={() => onEditTodo && onEditTodo(todo, 'daily', {})}
                      className={`flex-1 text-left text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}
                    >{todo.text}</button>
                  </div>
                ))
              )}
            </div>

            {/* Add New Todo - only for today or future */}
            {dayTodosDate && !isDateInPast(new Date(dayTodosDate)) ? (
            <div className="flex gap-2 pt-3 border-t border-slate-200">
              <input
                type="text"
                value={newDayTodoText}
                onChange={(e) => setNewDayTodoText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addDayTodo()}
                placeholder="Neue Aufgabe..."
                className="flex-1 p-3 border border-slate-200 rounded-lg text-sm"
                autoFocus
              />
              <Button onClick={addDayTodo} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            ) : (
            <div className="text-xs text-slate-400 text-center py-3 border-t border-slate-200">Vergangener Tag – nicht mehr bearbeitbar</div>
            )}
            
            {/* Button to show full week */}
            <button 
              onClick={() => { setShowDayTodosModal(false); setShowWeekTodosOverview(true); }} 
              className="w-full mt-3 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
            >
              Ganze Woche anzeigen →
            </button>
          </div>
        </div>
      )}

      {/* Week Todos Overview Modal */}
      {showWeekTodosOverview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 max-w-5xl w-full shadow-xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-purple-600" />
                Wochen-Aufgaben - KW {weekNumber}
              </h3>
              <button onClick={() => setShowWeekTodosOverview(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Week Days Grid - 4 columns on desktop for 8 tiles (4/4 layout) */}
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {(() => {
                  const grouped = getWeekTodosGrouped();
                  const now = new Date();
                  const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1;
                  
                  // Create tiles: 7 weekdays + 1 "Diese Woche" (unassigned)
                  const tiles = [
                    ...DAYS_SHORT.map((dayName, dayIndex) => ({ 
                      key: dayIndex, 
                      name: dayName, 
                      isDay: true,
                      isToday: dayIndex === todayWeekday 
                    })),
                    { key: 'unassigned', name: 'Diese Woche', isDay: false, isToday: false }
                  ];
                  
                  return tiles.map((tile) => {
                    const todos = grouped[tile.key] || [];
                    const openTodos = todos.filter(t => !t.done).length;
                    const doneTodos = todos.filter(t => t.done).length;
                    
                    return (
                      <div 
                        key={tile.key}
                        className={`rounded-xl border-2 p-2 min-w-0 ${
                          tile.isToday 
                            ? 'border-emerald-400 bg-emerald-50' 
                            : tile.key === 'unassigned'
                              ? 'border-purple-300 bg-purple-50'
                              : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        {/* Day Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className={`text-xs font-bold ${
                              tile.isToday ? 'text-emerald-700' : 
                              tile.key === 'unassigned' ? 'text-purple-700' : 'text-slate-700'
                            }`}>
                              {tile.name}
                            </span>
                            {tile.isToday && (
                              <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">
                                Heute
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-[10px]">
                            {openTodos > 0 && (
                              <span className={`px-1.5 py-0.5 rounded-full ${
                                tile.key === 'unassigned' ? 'bg-purple-200 text-purple-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {openTodos}
                              </span>
                            )}
                            {doneTodos > 0 && (
                              <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                                ✓{doneTodos}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Todos List */}
                        <div className="space-y-1 mb-2 max-h-[150px] overflow-y-auto">
                          {todos.length === 0 ? (
                            <p className="text-slate-400 text-[10px] text-center py-2">Keine Aufgaben</p>
                          ) : (
                            todos.map(todo => (
                              <div
                                key={todo.id}
                                className={`p-1.5 rounded-lg flex items-center gap-1.5 text-xs transition-colors ${
                                  todo.done 
                                    ? 'bg-white/50 text-slate-400' 
                                    : 'bg-white text-slate-700 hover:bg-purple-50'
                                }`}
                              >
                                <button
                                  onClick={() => toggleWeekTodo(todo.id, todo.source, tile.key)}
                                  className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                    todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-purple-400'
                                  }`}
                                >
                                  {todo.done && <Check className="w-2 h-2 text-white" />}
                                </button>
                                <button
                                  onClick={() => onEditTodo && onEditTodo(todo, 'weekly', { weekKey: tile.key })}
                                  className={`flex-1 truncate text-left text-xs ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}
                                >{todo.text}</button>
                                <button
                                  onClick={() => deleteWeekTodo(todo.id, todo.source)}
                                  className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Add Todo Input */}
                        <div className="flex gap-1">
                          {(tile.key === 'unassigned' || !isDateInPast(weekDates[tile.key])) ? (
                          <>
                          <input
                            type="text"
                            value={selectedWeekday === tile.key ? newWeekTodoText : ''}
                            onChange={(e) => {
                              setSelectedWeekday(tile.key);
                              setNewWeekTodoText(e.target.value);
                            }}
                            onFocus={() => setSelectedWeekday(tile.key)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setSelectedWeekday(tile.key);
                                addWeekTodo(tile.key);
                              }
                            }}
                            placeholder="+ Neu..."
                            className="flex-1 min-w-0 p-1.5 border border-slate-200 rounded text-[10px] bg-white"
                          />
                          <button 
                            onClick={() => {
                              setSelectedWeekday(tile.key);
                              addWeekTodo(tile.key);
                            }}
                            className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded flex-shrink-0"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                          </>
                          ) : (
                          <div className="text-[10px] text-slate-400 text-center py-1 w-full">Vergangen</div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-200 mt-4">
              <div className="text-sm text-slate-500">
                {(() => {
                  const grouped = getWeekTodosGrouped();
                  let total = 0, done = 0;
                  Object.values(grouped).forEach(todos => {
                    total += todos.length;
                    done += todos.filter(t => t.done).length;
                  });
                  return `${done}/${total} Aufgaben erledigt`;
                })()}
              </div>
              <Button onClick={() => setShowWeekTodosOverview(false)} variant="outline">
                Schließen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Modal */}
      <AppointmentModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingAppointment(null); }}
        onSave={handleSaveAppointment}
        onDelete={handleDeleteAppointment}
        initialData={editingAppointment}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        checkOverlap={checkOverlapForDate}
        calendars={googleCalendars}
        primaryCalendar={primaryCalendar}
        onToggleDone={onToggleAppointmentDone}
        isToday={editingAppointment?.date === getLocalDateStr()}
      />

      {/* Tip */}
      {showCalendarTip && (
        <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
          <p className="text-sm text-emerald-700 mb-2">
            <strong>💡 Tipp:</strong> Tippe auf ein Zeitfeld um einen neuen Termin anzulegen. Tippe auf einen Termin-Block um ihn zu bearbeiten oder zu löschen.
          </p>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 accent-emerald-600"
              onChange={e => {
                if (e.target.checked) {
                  userStorage.setItem('burningteam_hide_calendar_tip', 'true');
                  setShowCalendarTip(false);
                }
              }}
            />
            <span className="text-xs text-emerald-600">Nicht mehr anzeigen</span>
          </label>
        </div>
      )}
    </div>
  );
}

